// Constants
import { MruStoreCollectionKey, StoreEntityStatus, MyDayUser, StoreTableNames } from "@ms/uno-constants/lib/local/StoreConstants";
import { SubRouteType, SubRouteTypeToViewNameMap } from "@ms/uno-constants/lib/local/RoutingConstants";
import { PersistedObjectStoreIndexNames } from "@ms/uno-constants/lib/local/PersistenceConstants";
import { ClientFlavor } from "@ms/uno-constants/lib/local/AppConstants";
import { ViewDataFetchState, ViewName } from "@ms/uno-constants/lib/local/ViewConstants";
import { DefaultMyTasksViewPivotType, MyTasksViewPivotType } from "@ms/uno-constants/lib/local/ViewPivots";
import { Store } from "@ms/uno-stores/lib/local/stores/Store";
import { TraceLevel } from "@ms/uno-telemetry/lib/local/events/Trace.event";
import { MarkerTag } from "@ms/uno-telemetry/lib/local/performance/constants/PerformanceMarkerConstants";
import { getCurrentDateWithoutTimestamp } from "@ms/uno-utilities/lib/local/DateUtilities";
import { extractMyTasksRoutingDetailsFromSubRoute } from "@ms/uno-routing/lib/local/utilities/RouteUtilities";
// Error
import { ErrorUtilities } from "@ms/uno-errors/lib/local/utilities/ErrorUtilities";
export var StoreHydrationCheckpoints;
(function(StoreHydrationCheckpoints) {
    // Store Hydration starts for the view
    StoreHydrationCheckpoints["Start"] = "Start";
    // Store Hydration ends for the view with success
    StoreHydrationCheckpoints["EndWithSuccess"] = "EndWithSuccess";
    // Store Hydration ends for the view with failure
    StoreHydrationCheckpoints["EndWithFailure"] = "EndWithFailure";
    // Store Hydration ends for the view with non-actionable state
    // This will cover the cases where status is not present in the cache or no entries in the cache
    StoreHydrationCheckpoints["EndNonActionable"] = "EndNonActionable";
})(StoreHydrationCheckpoints || (StoreHydrationCheckpoints = {}));
/** Worker to hydrate the store by reading the persisted data from browser cache */ export class StoreHydrationWorker {
    async init() {
        if (this.initialized) {
            return;
        }
        // Load Mru data on init
        if (this.configProvider().settings.persistenceSettings.mru && this.configProvider().sessionMetaData.appMetadata.clientFlavor === ClientFlavor.App) {
            this.hydrateUnoLeftNavData();
        }
        // For loading data other than Mru
        if (this.configProvider().flights.EnableTasksDataPersistence && this.configProvider().settings.persistenceSettings.tasks) {
            this.routeStore.subscribeToStore(this.handleRouteStoreChange);
            this.loadPersistedDataAsPerRouteType();
        }
        this.initialized = true;
    }
    /**
     * Stops the worker
     */ stop() {
        if (this.configProvider().flights.EnableTasksDataPersistence && this.configProvider().settings.persistenceSettings.tasks) {
            this.routeStore.unsubscribeFromStore(this.handleRouteStoreChange);
        }
        this.initialized = false;
    }
    /**
     * Constructor for the StoreHydrationWorker
     * @param storeLoader The store loader
     * @param persistedStoreLoader persisted stores loader
     * @param configProvider The config provider
     * @param persistentAsyncStorage persisted async storage instance
     * @param loggers The loggers context
     */ constructor(storeLoader, persistedStoreLoader, configProvider, persistentAsyncStorage, loggers){
        this.storeLoader = storeLoader;
        this.persistedStoreLoader = persistedStoreLoader;
        this.configProvider = configProvider;
        this.persistentAsyncStorage = persistentAsyncStorage;
        this.loggers = loggers;
        this.initialized = false;
        this.routeStore = this.storeLoader.load(Store.Route);
        this.performanceCollectionStore = this.storeLoader.load(Store.PerformanceCollection);
        this.loadPersistedDataAsPerRouteType = ()=>{
            const subRouteType = this.routeStore.getCurrentSubRouteType();
            if (!this.shouldHydrateFromPersistentStorage(SubRouteTypeToViewNameMap[subRouteType])) {
                this.loggers.traceLogger.logTrace(0x1e2463d8 /* tag_4jgpy */ , TraceLevel.Verbose, `loadPersistedDataAsPerRouteType - No need to hydrate from cache [subRouteType=${subRouteType}]`);
                return;
            }
            const currentSubRoute = this.routeStore.getCurrentSubRoute();
            switch(subRouteType){
                case SubRouteType.MyDay:
                    this.hydrateMyDayPageData();
                    break;
                case SubRouteType.MyTasks:
                    this.hydrateMyTasksPageData(currentSubRoute, subRouteType);
                    break;
                case SubRouteType.MyPlans:
                    break;
                case SubRouteType.BasicPlan:
                    this.hydrateBasicPlansPageData(currentSubRoute[0]);
                    break;
                default:
                    break;
            }
        };
        this.handleRouteStoreChange = ()=>{
            this.loadPersistedDataAsPerRouteType();
        };
        this.shouldHydrateFromPersistentStorage = (viewName)=>{
            return this.performanceCollectionStore.getViewDataFetchState(viewName) !== ViewDataFetchState.Completed;
        };
        this.hydrateMyDayPageData = async ()=>{
            try {
                this.loggers.performanceMarker.mark(MarkerTag.StoreHydration, {
                    checkpoint: "Start",
                    viewName: ViewName.MyDayPage
                });
                const [tasksInMyDayStatus, tasks] = await Promise.all([
                    this.persistentAsyncStorage.read(StoreTableNames.TasksInMyDayStatusTable, MyDayUser.CURRENT_USER),
                    this.persistentAsyncStorage.readByIndex(StoreTableNames.Tasks, PersistedObjectStoreIndexNames.TasksMyDayDateIndex, getCurrentDateWithoutTimestamp())
                ]);
                if (tasksInMyDayStatus?.status !== StoreEntityStatus.Present) {
                    this.loggers.traceLogger.logTrace(0x1e247054 /* tag_4jhbu */ , TraceLevel.Info, `MyDayPage collection status not present in cache [status=${tasksInMyDayStatus?.status}]`);
                    this.loggers.performanceMarker.mark(MarkerTag.StoreHydration, {
                        checkpoint: "EndNonActionable",
                        viewName: ViewName.MyDayPage
                    });
                    return;
                }
                await this.hydrateDataIntoStore(SubRouteType.MyDay, Store.BasicTask, {
                    tableName: StoreTableNames.Tasks,
                    data: tasks
                }, {
                    statusTableName: StoreTableNames.TasksInMyDayStatusTable,
                    statusData: tasksInMyDayStatus
                }, (tasks)=>this.fetchTasksFromPersistentStorage(tasks));
                this.loggers.performanceMarker.mark(MarkerTag.StoreHydration, {
                    checkpoint: "EndWithSuccess",
                    viewName: ViewName.MyDayPage
                });
            } catch (error) {
                this.loggers.traceLogger.logTrace(0x1e247050 /* tag_4jhbq */ , TraceLevel.Error, `hydrateMyDayPageData [Error=${error}]`);
                this.loggers.performanceMarker.mark(MarkerTag.StoreHydration, {
                    checkpoint: "EndWithFailure",
                    viewName: ViewName.MyDayPage
                });
            }
        };
        this.hydrateMyTasksATMPageData = async ()=>{
            const userId = this.storeLoader.load(Store.AppContext).getAppContext().userId;
            if (userId) {
                const [tasks, taskStatus] = await Promise.all([
                    this.persistentAsyncStorage.readByIndex(StoreTableNames.Tasks, PersistedObjectStoreIndexNames.TasksAssignmentsIndex, userId),
                    this.persistentAsyncStorage.read(StoreTableNames.TasksByAssigneeStatus, userId)
                ]);
                if (taskStatus?.status !== StoreEntityStatus.Present) {
                    const error = {
                        message: `AssignedToMe task status not present in cache [status=${taskStatus?.status}]`,
                        isNonActionable: true,
                        traceType: TraceLevel.Info
                    };
                    throw error;
                }
                await this.hydrateDataIntoStore(SubRouteType.MyTasks, Store.BasicTask, {
                    tableName: StoreTableNames.Tasks,
                    data: tasks
                }, {
                    statusTableName: StoreTableNames.TasksByAssigneeStatus,
                    statusData: taskStatus
                }, (tasks)=>this.fetchTasksFromPersistentStorage(tasks));
            } else {
                const error = {
                    message: "UserId is not available",
                    isNonActionable: true,
                    traceType: TraceLevel.Info
                };
                throw error;
            }
        };
        this.hydrateMyTasksFlaggedEmailsPageData = async ()=>{
            await this.persistedStoreLoader(Store.BasicPlan);
            const basicPlanStore = this.storeLoader.load(Store.BasicPlan);
            const planId = basicPlanStore?.getFlaggedEmailPlanId();
            if (planId) {
                const [tasks, taskStatus] = await Promise.all([
                    this.persistentAsyncStorage.readByIndex(StoreTableNames.Tasks, PersistedObjectStoreIndexNames.TasksPlanIndex, planId),
                    this.persistentAsyncStorage.read(StoreTableNames.TasksInPlanStatus, planId)
                ]);
                if (taskStatus?.status !== StoreEntityStatus.Present) {
                    const error = {
                        message: `FlaggedEmail task status not present in cache [status=${taskStatus?.status}]`,
                        isNonActionable: true,
                        traceType: TraceLevel.Info
                    };
                    throw error;
                }
                await this.hydrateDataIntoStore(SubRouteType.MyTasks, Store.BasicTask, {
                    tableName: StoreTableNames.Tasks,
                    data: tasks
                }, {
                    statusTableName: StoreTableNames.TasksInPlanStatus,
                    statusData: taskStatus
                }, (tasks)=>this.fetchTasksFromPersistentStorage(tasks));
            } else {
                const error = {
                    message: "FlaggedEmailPlanId is not available",
                    isNonActionable: true,
                    traceType: TraceLevel.Info
                };
                throw error;
            }
        };
        this.hydrateMyTasksPrivateTasksPageData = async ()=>{
            await this.persistedStoreLoader(Store.BasicPlan);
            const basicPlanStore = this.storeLoader.load(Store.BasicPlan);
            const planId = basicPlanStore?.getDefaultPlanId();
            if (planId) {
                const [tasks, taskStatus] = await Promise.all([
                    this.persistentAsyncStorage.readByIndex(StoreTableNames.Tasks, PersistedObjectStoreIndexNames.TasksPlanIndex, planId),
                    this.persistentAsyncStorage.read(StoreTableNames.TasksInPlanStatus, planId)
                ]);
                if (taskStatus?.status !== StoreEntityStatus.Present) {
                    const error = {
                        message: `Private task status not present in cache [status=${taskStatus?.status}]`,
                        isNonActionable: true,
                        traceType: TraceLevel.Info
                    };
                    throw error;
                }
                await this.hydrateDataIntoStore(SubRouteType.MyTasks, Store.BasicTask, {
                    tableName: StoreTableNames.Tasks,
                    data: tasks
                }, {
                    statusTableName: StoreTableNames.TasksInPlanStatus,
                    statusData: taskStatus
                }, (tasks)=>this.fetchTasksFromPersistentStorage(tasks));
            } else {
                const error = {
                    message: "DefaultPlanId is not available",
                    isNonActionable: true,
                    traceType: TraceLevel.Info
                };
                throw error;
            }
        };
        this.hydrateMyTasksPageData = async (currentSubRoute, subRouteType)=>{
            const routingDetails = extractMyTasksRoutingDetailsFromSubRoute(currentSubRoute, subRouteType);
            const pivot = routingDetails?.pivotType ?? DefaultMyTasksViewPivotType;
            try {
                this.loggers.performanceMarker.mark(MarkerTag.StoreHydration, {
                    checkpoint: "Start",
                    viewName: ViewName.MyTasksPage
                });
                switch(pivot){
                    case MyTasksViewPivotType.AssignedToMe:
                        await this.hydrateMyTasksATMPageData();
                        break;
                    case MyTasksViewPivotType.FlaggedEmails:
                        await this.hydrateMyTasksFlaggedEmailsPageData();
                        break;
                    case MyTasksViewPivotType.PrivateTasks:
                        await this.hydrateMyTasksPrivateTasksPageData();
                        break;
                    case MyTasksViewPivotType.All:
                        await Promise.all([
                            this.hydrateMyTasksATMPageData(),
                            this.hydrateMyTasksFlaggedEmailsPageData(),
                            this.hydrateMyTasksPrivateTasksPageData()
                        ]);
                        break;
                    default:
                        ErrorUtilities.unreachableCase(pivot, (log)=>this.loggers.traceLogger.logTrace(0x1e241782 /* tag_4jb4c */ , TraceLevel.Error, log));
                        break;
                }
                this.loggers.performanceMarker.mark(MarkerTag.StoreHydration, {
                    checkpoint: "EndWithSuccess",
                    viewName: ViewName.MyTasksPage
                });
            } catch (error) {
                const failedResult = error;
                let extraData = {};
                const traceType = failedResult.traceType ?? TraceLevel.Error;
                const errorMessage = failedResult.message ?? error;
                if (failedResult.isNonActionable) {
                    extraData = {
                        checkpoint: "EndNonActionable",
                        viewName: ViewName.MyTasksPage
                    };
                } else {
                    extraData = {
                        checkpoint: "EndWithFailure",
                        viewName: ViewName.MyTasksPage
                    };
                }
                this.loggers.performanceMarker.mark(MarkerTag.StoreHydration, extraData);
                this.loggers.traceLogger.logTrace(0x1e2148ce /* tag_4iu9o */ , traceType, `hydrateMyTasksPageData [Error=${errorMessage}]`);
            }
        };
        this.hydrateBasicPlansPageData = async (planId)=>{
            try {
                this.loggers.performanceMarker.mark(MarkerTag.StoreHydration, {
                    checkpoint: "Start",
                    viewName: ViewName.BasicPlanPage
                });
                const [plan, planStatus, tasks, tasksInPlanStatus, buckets, bucketInPlanStatus] = await Promise.all([
                    this.persistentAsyncStorage.read(StoreTableNames.Plans, planId),
                    this.persistentAsyncStorage.read(StoreTableNames.PlanStatus, planId),
                    this.persistentAsyncStorage.readByIndex(StoreTableNames.Tasks, PersistedObjectStoreIndexNames.TasksPlanIndex, planId),
                    this.persistentAsyncStorage.read(StoreTableNames.TasksInPlanStatus, planId),
                    this.persistentAsyncStorage.readByIndex(StoreTableNames.Buckets, PersistedObjectStoreIndexNames.BucketsPlanIndex, planId),
                    this.persistentAsyncStorage.read(StoreTableNames.BucketsInPlanStatus, planId)
                ]);
                if (planStatus?.status !== StoreEntityStatus.Present || tasksInPlanStatus?.status !== StoreEntityStatus.Present || bucketInPlanStatus?.status !== StoreEntityStatus.Present) {
                    this.loggers.traceLogger.logTrace(0x1e24209f /* tag_4jcc5 */ , TraceLevel.Info, `BasicPlanPage collection status not present [Planstatus=${planStatus?.status}][TasksInPlanstatus=${tasksInPlanStatus?.status}][BucketInPlanStatus=${bucketInPlanStatus?.status}]`);
                    this.loggers.performanceMarker.mark(MarkerTag.StoreHydration, {
                        checkpoint: "EndNonActionable",
                        viewName: ViewName.BasicPlanPage
                    });
                    return;
                }
                if (!plan) {
                    this.loggers.traceLogger.logTrace(0x1e2416c4 /* tag_4jb1e */ , TraceLevel.Info, `BasicPlanPage - No entries in cache [Store=BasicPlanStore]`);
                    this.loggers.performanceMarker.mark(MarkerTag.StoreHydration, {
                        checkpoint: "EndNonActionable",
                        viewName: ViewName.BasicPlanPage
                    });
                    return;
                }
                const basicPlanHydration = [
                    this.hydrateDataIntoStore(SubRouteType.BasicPlan, Store.BasicPlan, {
                        tableName: StoreTableNames.Plans,
                        data: {
                            [plan.id]: plan
                        }
                    }, {
                        statusTableName: StoreTableNames.PlanStatus,
                        statusData: planStatus
                    }, (plan)=>this.fetchPlansFromPersistentStorage(plan)),
                    this.hydrateDataIntoStore(SubRouteType.BasicPlan, Store.BasicTask, {
                        tableName: StoreTableNames.Tasks,
                        data: tasks
                    }, {
                        statusTableName: StoreTableNames.TasksInPlanStatus,
                        statusData: tasksInPlanStatus
                    }, (tasks)=>this.fetchTasksFromPersistentStorage(tasks)),
                    this.hydrateDataIntoStore(SubRouteType.BasicPlan, Store.BasicBucket, {
                        tableName: StoreTableNames.Buckets,
                        data: buckets
                    }, {
                        statusTableName: StoreTableNames.BucketsInPlanStatus,
                        statusData: bucketInPlanStatus
                    })
                ];
                await Promise.all(basicPlanHydration);
                this.loggers.performanceMarker.mark(MarkerTag.StoreHydration, {
                    checkpoint: "EndWithSuccess",
                    viewName: ViewName.BasicPlanPage
                });
            } catch (error) {
                this.loggers.traceLogger.logTrace(0x1e256853 /* tag_4jw7t */ , TraceLevel.Error, `hydrateBasicPlansPageData [Error=${error}]`);
                this.loggers.performanceMarker.mark(MarkerTag.StoreHydration, {
                    checkpoint: "EndWithFailure",
                    viewName: ViewName.BasicPlanPage
                });
            }
        };
        this.fetchTasksFromPersistentStorage = async (tasks)=>{
            const taskIds = Object.values(tasks).map((task)=>task.id);
            const operations = {
                [StoreTableNames.TaskDetails]: taskIds,
                [StoreTableNames.TaskboardTaskFormatForAssignedTo]: taskIds,
                [StoreTableNames.TaskboardTaskFormatForBucket]: taskIds,
                [StoreTableNames.TaskboardTaskFormatForProgress]: taskIds
            };
            const data = {
                [StoreTableNames.Tasks]: tasks
            };
            const batchReadData = await this.persistentAsyncStorage.batchRead(operations);
            Object.assign(data, batchReadData);
            return data;
        };
        this.fetchPlansFromPersistentStorage = async (plans)=>{
            const planIds = Object.values(plans).map((plan)=>plan.id);
            const operations = {
                [StoreTableNames.PlanDetails]: planIds
            };
            const data = {
                [StoreTableNames.Plans]: plans
            };
            const batchReadData = await this.persistentAsyncStorage.batchRead(operations);
            Object.assign(data, batchReadData);
            return data;
        };
        this.hydrateDataIntoStore = async (subRouteType, storeType, coreData, statusDetails, fetchDataFn)=>{
            if (Object.keys(coreData.data).length > 0) {
                const store = await this.persistedStoreLoader(storeType);
                let isDataChanged = false;
                if (fetchDataFn) {
                    const fetchedData = await fetchDataFn(coreData.data);
                    isDataChanged = Object.keys(fetchedData).map((key)=>store.getPersistedTables()[key]?.hydrate(fetchedData[key]) ?? false).some(Boolean);
                } else {
                    isDataChanged = store.getPersistedTables()[coreData.tableName]?.hydrate(coreData.data) ?? false;
                }
                const isStatusChanged = store.getPersistedTables()[statusDetails.statusTableName]?.hydrate({
                    [statusDetails.statusData.key]: statusDetails.statusData
                });
                this.loggers.traceLogger.logTrace(0x1e24209d /* tag_4jcc3 */ , TraceLevel.Info, `StoreChangeDetails [SubRouteType=${subRouteType}][Store=${storeType}][DataChanged=${isDataChanged}][StatusChanged=${isStatusChanged}]`);
                if (isDataChanged || isStatusChanged) {
                    store.publishStoreChangedEvent();
                    this.loggers.traceLogger.logTrace(0x1e24209c /* tag_4jcc2 */ , TraceLevel.Info, `StoreHydrated [SubRouteType=${subRouteType}][Store=${storeType}]`);
                } else {
                    this.loggers.traceLogger.logTrace(0x1e24209b /* tag_4jcc1 */ , TraceLevel.Info, `Tried hydration, store unchanged [SubRouteType=${subRouteType}][Store=${storeType}]`);
                }
            } else {
                this.loggers.traceLogger.logTrace(0x1e24209a /* tag_4jcc0 */ , TraceLevel.Info, `No entries in cache [SubRouteType=${subRouteType}][Store=${storeType}]`);
            }
        };
        this.hydrateUnoLeftNavData = async ()=>{
            try {
                this.loggers.performanceMarker.mark(MarkerTag.StoreHydration, {
                    checkpoint: "Start",
                    viewName: ViewName.UnoLeftNavContainer
                });
                const [mruStatus, items] = await Promise.all([
                    this.persistentAsyncStorage.read(StoreTableNames.MruCollectionStatus, MruStoreCollectionKey.MRU_COLLECTION),
                    this.persistentAsyncStorage.readAll(StoreTableNames.Mru)
                ]);
                if (Object.keys(items).length > 0) {
                    const mruStore = await this.persistedStoreLoader(Store.Mru);
                    const isMruItemsChanged = mruStore.getPersistedTables()[StoreTableNames.Mru]?.hydrate(items);
                    const isMruItemsCollectionChanged = mruStatus ? mruStore.getPersistedTables()[StoreTableNames.MruCollectionStatus]?.hydrate({
                        [MruStoreCollectionKey.MRU_COLLECTION]: mruStatus
                    }) : false;
                    this.loggers.traceLogger.logTrace(0x1e249293 /* tag_4jjkt */ , TraceLevel.Info, `StoreChangeDetails [Store=MruStore][isMruItemsChanged=${isMruItemsChanged}][isMruItemsCollectionChanged=${isMruItemsCollectionChanged}][mruCollectionStatus=${mruStatus?.status}]`);
                    if (isMruItemsChanged || isMruItemsCollectionChanged) {
                        mruStore.publishStoreChangedEvent();
                        this.loggers.traceLogger.logTrace(0x1e255687 /* tag_4jv0h */ , TraceLevel.Info, `StoreHydrated [Store=MruStore]`);
                        this.loggers.performanceMarker.mark(MarkerTag.StoreHydration, {
                            checkpoint: "EndWithSuccess",
                            viewName: ViewName.UnoLeftNavContainer
                        });
                    } else {
                        this.loggers.traceLogger.logTrace(0x1e255686 /* tag_4jv0g */ , TraceLevel.Info, `Tried hydration, store unchanged [Store=MruStore]]`);
                        this.loggers.performanceMarker.mark(MarkerTag.StoreHydration, {
                            checkpoint: "EndNonActionable",
                            viewName: ViewName.UnoLeftNavContainer
                        });
                    }
                } else {
                    this.loggers.traceLogger.logTrace(0x1e255685 /* tag_4jv0f */ , TraceLevel.Info, `No entries in cache [Store=MruStore]`);
                    this.loggers.performanceMarker.mark(MarkerTag.StoreHydration, {
                        checkpoint: "EndNonActionable",
                        viewName: ViewName.UnoLeftNavContainer
                    });
                }
            } catch (error) {
                this.loggers.traceLogger.logTrace(0x1e256852 /* tag_4jw7s */ , TraceLevel.Error, `hydrateAllMruData [Error=${error}]`);
                this.loggers.performanceMarker.mark(MarkerTag.StoreHydration, {
                    checkpoint: "EndWithFailure",
                    viewName: ViewName.UnoLeftNavContainer
                });
            }
        };
    }
}
